import React, { useContext, useEffect, useState } from "react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { styled, formGap, iconButtonSize, StyledFieldLabel, Header3, muiInputFormsCSS } from "@iventis/styles";
import { LinearProgress, TextField, useTheme } from "@mui/material";
import { Theme } from "@emotion/react";
import { Content } from "@iventis/translations";
import { ErrorText } from "@iventis/components/src/selectors/incremental-value-input";
import { CustomDialog, CustomRadioSelector, FormButtonsComponent, StatusIcon } from "@iventis/components";
import { SitemapVersionLevelUploadMethod } from "@iventis/domain-model/model/sitemapVersionLevelUploadMethod";
import { SitemapVersionLevelCreateResponse } from "@iventis/domain-model/model/sitemapVersionLevelCreateResponse";
import { MapObjectSitemapLevelReassignmentPayload } from "@iventis/domain-model/model/mapObjectSitemapLevelReassignmentPayload";
import { RemoteUploadContext } from "@iventis/remote-upload";
import { SitemapVersionLevelStatus } from "@iventis/domain-model/model/sitemapVersionLevelStatus";
import { CadEditorWrapper } from "@iventis/cad-georeferencer/src/cad-editor-wrapper";
import { CadConfig } from "@iventis/cad-georeferencer/src/types/cad-config";
import { SitemapVersionLevelCadConfig } from "@iventis/domain-model/model/sitemapVersionLevelCadConfig";
import { useCadApiContext } from "@iventis/cad-georeferencer/src/context/cad-api-context";
import { ConfirmationDialogComponent } from "@iventis/components/src/confirmation-dialog";
import { useSitemapVersionLevel } from "./hooks/use-sitemap-version-level";
import { useSitemapRoutingContext } from "./context/sitemap-routing-context";
import { SitemapVersionLevelMbTilesForm } from "./sitemap-version-level-mbtiles";
import { SitemapVersionLevelCadForm } from "./sitemap-version-level-cad";
import { LevelReassignmentForm } from "./sitemap-version-level-reassignment-form";
import { useSitemapApiContext } from "./context/sitemap-api-context";
import { useSitemapVersionLevelCadFiles } from "./hooks/use-sitemap-version-level-cad-files";
import { TileUploader } from "./sitemap-tile-uploader";
import { SitemapVersionLevelCad, SitemapVersionLevelWithFiles } from "./sitemap-types";

const TIME_BETWEEN_REFETCHES = 15000;

interface SitemapVersionLevelFormProps {
    projectName?: string;
}

export const SitemapVersionLevelForm = ({ projectName }: SitemapVersionLevelFormProps = {}) => {
    const translate = useIventisTranslate();

    const { setSelectedSitemapVersionLevelId } = useSitemapRoutingContext();

    const {
        sitemap,
        parentSitemapVersion,
        sitemapVersionLevel,
        setSitemapVersionLevel,
        isSitemapVersionLevelLoading,
        canSitemapVersionLevelSave,
        saveSitemapVersionLevel: saveSitemapVersionLevelReq,
        isSavingSitemapVersionLevel,
        isLevelUnique,
        isLevelReassignmentNeeded,
        refetch,
    } = useSitemapVersionLevel();
    const { reassignSitemapLevels, getSitemapVersionLevelCadConfig, getMapBackgrounds, getSitemapVersionLevelAsset } = useSitemapApiContext();
    const { style } = useSitemapVersionLevelCadFiles(sitemapVersionLevel);

    const [sitemapVersionLevelIdForReassignment, setSitemapVersionLevelIdForReassignment] = useState<string | null>(null);
    const [showExistingLevelWarningDialog, setShowExistingLevelWarningDialog] = useState(false);
    const [tileUploadRequest, setTileUploadRequest] = useState<SitemapVersionLevelCreateResponse & SitemapVersionLevelWithFiles>(null);
    const remoteUploadContext = useContext(RemoteUploadContext);
    const theme = useTheme<Theme>();
    const cadApiContext = useCadApiContext();
    const saveSitemapVersionLevel = async () => {
        const response = await saveSitemapVersionLevelReq(sitemapVersionLevel);
        // Empty string is returned if we are not updating the tiles
        if (response != null && response.url != null && response.url.length > 0) {
            const fileToUpload = sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles ? sitemapVersionLevel.mbtiles : sitemapVersionLevel.cad;
            const contentType = sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles ? "application/vnd.mapbox-vector-tile" : "application/acad";
            remoteUploadContext.upload(response.id, response.url, fileToUpload, contentType, () => {
                const updatedCadConfig: SitemapVersionLevelCadConfig =
                    sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Cad
                        ? { ...sitemapVersionLevel.cadConfig, sitemapVersionLevelId: response.id, version: sitemapVersionLevel.cadConfig.version ?? 2 }
                        : null;
                const updatedSitemapVersionLevel = {
                    ...sitemapVersionLevel,
                    cadConfig: updatedCadConfig,
                    id: response.id,
                    status: sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles ? SitemapVersionLevelStatus.Tiling : SitemapVersionLevelStatus.Extracting,
                };
                // Once the upload has finshed need to update the SitemapVersionLevel status, depending on what upload type it is
                setSitemapVersionLevel(updatedSitemapVersionLevel);
                saveSitemapVersionLevelReq(updatedSitemapVersionLevel);
                setTileUploadRequest(null);
            });
            setTileUploadRequest({
                ...sitemapVersionLevel,
                id: response.id,
                fileUploadUrl: response.url,
                perimeter: sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles ? JSON.parse(sitemapVersionLevel.perimeter) : null,
            });
        }
    };

    const handleFinishCadEditor = async (cadConfig: CadConfig) => {
        if (sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Cad) {
            const updatedSitemapVersionLevel = {
                ...sitemapVersionLevel,
                status: SitemapVersionLevelStatus.Tiling,
                perimeter: JSON.stringify(cadConfig.perimeter),
                bounding: cadConfig.bounding,
                cadConfig: {
                    controlPoints: cadConfig.controlPoints,
                    crop: cadConfig.crop,
                    excludedLayerIds: cadConfig.excludedLayerIds,
                    sitemapVersionLevelId: sitemapVersionLevel.id,
                    sourceEpsgCode: sitemapVersionLevel.cadConfig.sourceEpsgCode,
                    unitOfMeasure: cadConfig.unitOfMeasure,
                    version: sitemapVersionLevel.cadConfig.version,
                },
            };
            setSitemapVersionLevel(updatedSitemapVersionLevel);
            await saveSitemapVersionLevelReq(updatedSitemapVersionLevel);
            await cadApiContext.shutdownApi(); // Shutdown the cad api to release resources
        }
        // Patch sitemap version level with the cad config
    };

    const performCurrentSitemapVersionLevelSaveProcess = () => {
        const allSitemapVersionLevels = sitemap.versions.flatMap((v) => v.sitemapVersionLevels);

        const doesSitemapHaveAnotherLevelWithSameIndex = allSitemapVersionLevels.some(
            ({ id, levelIndex }) => id !== sitemapVersionLevel.id && levelIndex === sitemapVersionLevel.levelIndex
        );

        if (isLevelReassignmentNeeded() && !doesSitemapHaveAnotherLevelWithSameIndex) {
            setSitemapVersionLevelIdForReassignment(sitemapVersionLevel.id);
        } else {
            saveSitemapVersionLevel();
        }
    };

    const handleSitemapVersionLevelSaveClicked = () => {
        const isCurrentLevelAlreadySavedOnVersion = parentSitemapVersion?.sitemapVersionLevels?.some(({ id }) => id === sitemapVersionLevel.id) ?? false;

        if (isCurrentLevelAlreadySavedOnVersion) {
            setShowExistingLevelWarningDialog(true);
            return;
        }

        performCurrentSitemapVersionLevelSaveProcess();
    };

    const handleExistingLevelWarningSaveClicked = () => {
        setShowExistingLevelWarningDialog(false);
        performCurrentSitemapVersionLevelSaveProcess();
    };

    const handleLevelReassignment = async (reassignments: Omit<MapObjectSitemapLevelReassignmentPayload, "projectId">[]) => {
        if (reassignments?.length > 0) {
            await reassignSitemapLevels(reassignments);
        }
        saveSitemapVersionLevel();
    };

    const onTileUploadFinshed = () => {
        remoteUploadContext.remove(tileUploadRequest.id);
        setTileUploadRequest(null);
    };

    const refetchUntilFinishedExtracting = async () => {
        await new Promise<void>((res) => setTimeout(() => res(), TIME_BETWEEN_REFETCHES));
        const { data: updatedSitemapVersionLevel } = await refetch();
        if (updatedSitemapVersionLevel.status !== SitemapVersionLevelStatus.Reviewing) {
            refetchUntilFinishedExtracting();
        }
    };

    const refetchUntilFinishedTiling = async () => {
        await new Promise<void>((res) => setTimeout(() => res(), TIME_BETWEEN_REFETCHES));
        const { data: updatedSitemapVersionLevel } = await refetch();
        if (updatedSitemapVersionLevel.status === SitemapVersionLevelStatus.Tiling) {
            refetchUntilFinishedTiling();
        }
    };

    useEffect(() => {
        if (sitemapVersionLevel?.status === SitemapVersionLevelStatus.Extracting) {
            refetchUntilFinishedExtracting();
        }
        if (sitemapVersionLevel?.status === SitemapVersionLevelStatus.Tiling) {
            refetchUntilFinishedTiling();
        }
    }, [sitemapVersionLevel?.status]);

    const getAvailableSitemapVersions = () => {
        if (!sitemap || !parentSitemapVersion) {
            return [];
        }

        return [
            ...sitemap.versions.filter((version) => version.id !== parentSitemapVersion.id),
            {
                ...parentSitemapVersion,
                sitemapVersionLevels: [...parentSitemapVersion.sitemapVersionLevels.filter((svl) => svl.id !== sitemapVersionLevelIdForReassignment)],
            },
        ];
    };

    if (!tileUploadRequest && (isSitemapVersionLevelLoading || sitemapVersionLevel == null)) {
        return <LinearProgress />;
    }
    return (
        <Container>
            <StyledForm>
                <div style={{ display: "flex", alignItems: "center", gap: formGap }}>
                    <Header3>
                        <span>
                            {sitemap?.name} / {parentSitemapVersion?.name} /{" "}
                        </span>
                        {sitemapVersionLevel?.levelName != null && sitemapVersionLevel?.levelName !== "" ? (
                            <span>{sitemapVersionLevel.levelName}</span>
                        ) : (
                            <span>{translate(Content.sitemaps.sitemapVersionLevel.level)}</span>
                        )}
                    </Header3>
                    {sitemapVersionLevel.status !== SitemapVersionLevelStatus.Uploading && (
                        <StatusIcon
                            height={25}
                            width={65}
                            fill={
                                sitemapVersionLevel.status === SitemapVersionLevelStatus.Active
                                    ? theme.secondaryColors.accept
                                    : sitemapVersionLevel.status === SitemapVersionLevelStatus.Failed
                                    ? theme.secondaryColors.error
                                    : theme.toastColours.info
                            }
                            label={translate(Content.sitemaps.sitemapVersion[sitemapVersionLevel.status])}
                        />
                    )}
                </div>
                {tileUploadRequest != null ? (
                    <TileUploader uploadRequests={[tileUploadRequest]} finish={onTileUploadFinshed} />
                ) : sitemapVersionLevel.status === SitemapVersionLevelStatus.Reviewing && style != null ? (
                    <GeoReferencerContainer>
                        <CadEditorWrapper
                            sourceEpsgCode={(sitemapVersionLevel as SitemapVersionLevelCad)?.cadConfig?.sourceEpsgCode}
                            style={style}
                            geoReferenced={
                                (sitemapVersionLevel as SitemapVersionLevelCad)?.cadConfig?.sourceEpsgCode != null ||
                                (sitemapVersionLevel as SitemapVersionLevelCad)?.cadConfig?.controlPoints != null
                            }
                            controlPoints={(sitemapVersionLevel as SitemapVersionLevelCad)?.cadConfig?.controlPoints}
                            onFinish={handleFinishCadEditor}
                            getMapBackgrounds={getMapBackgrounds}
                            getSitemapVersionLevelAsset={getSitemapVersionLevelAsset}
                        />
                    </GeoReferencerContainer>
                ) : sitemapVersionLevel.status === SitemapVersionLevelStatus.Active ||
                  sitemapVersionLevel.status === SitemapVersionLevelStatus.Failed ||
                  sitemapVersionLevel.status === SitemapVersionLevelStatus.Uploading ? (
                    <>
                        <div>
                            <CustomRadioSelector
                                defaultValueId={sitemapVersionLevel.uploadMethod}
                                onValueChange={(event) => setSitemapVersionLevel({ ...sitemapVersionLevel, uploadMethod: event.target.value as SitemapVersionLevelUploadMethod })}
                                options={[
                                    { id: SitemapVersionLevelUploadMethod.Cad, name: "DWG" },
                                    { id: SitemapVersionLevelUploadMethod.Mbtiles, name: "Mbtiles" },
                                ]}
                            />
                        </div>
                        <div className="level-details">
                            <div>
                                <StyledFieldLabel>{translate(Content.sitemaps.sitemapVersion.name)}</StyledFieldLabel>
                                <TextField
                                    inputProps={{ "data-testid": "sitemap-level-name-input" }}
                                    variant="outlined"
                                    value={sitemapVersionLevel.levelName ?? null}
                                    onChange={(event) => setSitemapVersionLevel({ ...sitemapVersionLevel, levelName: event.target.value })}
                                />
                            </div>
                            <div>
                                <StyledFieldLabel>{translate(Content.sitemaps.sitemapVersion.level_index)}</StyledFieldLabel>
                                <TextField
                                    inputProps={{ "data-testid": "sitemap-level-index-input", inputMode: "numeric", pattern: "^-?\\d*$" }}
                                    sx={{ maxWidth: "5rem" }}
                                    variant="outlined"
                                    value={sitemapVersionLevel.levelIndex === null || sitemapVersionLevel.levelIndex === undefined ? "" : sitemapVersionLevel.levelIndex}
                                    onChange={({ target: { value } }) => {
                                        if (value === "" || value === "-") {
                                            setSitemapVersionLevel({ ...sitemapVersionLevel, levelIndex: null });
                                        } else if (/^[-]?\d+$/.test(value)) {
                                            setSitemapVersionLevel({ ...sitemapVersionLevel, levelIndex: Number(value) });
                                        }
                                    }}
                                    type="number"
                                    error={!isLevelUnique}
                                />
                                {!isLevelUnique && <ErrorText>{translate(Content.sitemaps.sitemapVersion.level_index_must_be_unique)}</ErrorText>}
                            </div>
                            <div>
                                <StyledFieldLabel>{translate(Content.sitemaps.sitemapVersion.level_abbreviation)}</StyledFieldLabel>
                                <TextField
                                    inputProps={{ "data-testid": "sitemap-level-abv-input" }}
                                    variant="outlined"
                                    sx={{ maxWidth: "5rem" }}
                                    value={sitemapVersionLevel.levelAbbreviation ?? null}
                                    onChange={({ target: { value } }) => value?.length <= 3 && setSitemapVersionLevel({ ...sitemapVersionLevel, levelAbbreviation: value })}
                                />
                            </div>
                        </div>
                        {sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles && (
                            <SitemapVersionLevelMbTilesForm setSitemapVersionLevel={setSitemapVersionLevel} sitemapVersionLevel={sitemapVersionLevel} />
                        )}
                        {sitemapVersionLevel.uploadMethod === SitemapVersionLevelUploadMethod.Cad && (
                            <SitemapVersionLevelCadForm
                                setSitemapVersionLevel={setSitemapVersionLevel}
                                sitemapVersionLevel={sitemapVersionLevel}
                                getSitemapVersionLevelCadConfig={getSitemapVersionLevelCadConfig}
                                availableSitemapVersions={getAvailableSitemapVersions()}
                                sitemapId={sitemap?.id}
                            />
                        )}
                    </>
                ) : null}
            </StyledForm>
            {sitemapVersionLevel.status !== SitemapVersionLevelStatus.Reviewing &&
                sitemapVersionLevel.status !== SitemapVersionLevelStatus.Extracting &&
                sitemapVersionLevel.status !== SitemapVersionLevelStatus.Tiling && (
                    <StyledFormButtonsContainer>
                        <FormButtonsComponent
                            handleSubmit={handleSitemapVersionLevelSaveClicked}
                            isSubmitting={isSavingSitemapVersionLevel || tileUploadRequest != null}
                            disableSubmit={() => !canSitemapVersionLevelSave}
                            submitButtonText={isSavingSitemapVersionLevel || tileUploadRequest != null ? translate(Content.common.saving) : translate(Content.common.buttons.save)}
                            secondaryButtons={[{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setSelectedSitemapVersionLevelId(null) }]}
                        />
                    </StyledFormButtonsContainer>
                )}
            <CustomDialog open={sitemapVersionLevelIdForReassignment != null}>
                {sitemapVersionLevelIdForReassignment != null && (
                    <LevelReassignmentForm
                        availableSitemapVersions={getAvailableSitemapVersions()}
                        existingLevelsRemoved={[{ ...sitemapVersionLevel, perimeter: sitemapVersionLevel.perimeter ? JSON.parse(sitemapVersionLevel.perimeter) : null }]}
                        confirm={handleLevelReassignment}
                        close={() => setSitemapVersionLevelIdForReassignment(null)}
                    />
                )}
            </CustomDialog>
            <ConfirmationDialogComponent
                show={showExistingLevelWarningDialog}
                handleConfirm={handleExistingLevelWarningSaveClicked}
                handleCancel={() => setShowExistingLevelWarningDialog(false)}
                title="Are you sure you want to make these changes?"
                message={`You are editing a level on Project: ${projectName} | Sitemap: ${sitemap?.name}.`}
                confirmText={translate(Content.common.buttons.save)}
                cancelText={translate(Content.common.buttons.cancel)}
                dataTestId="confirm-edit-sitemap-level"
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;
    flex: 1;
`;

const StyledForm = styled.div`
    display: flex;
    min-height: 0;
    overflow-y: auto;
    gap: ${formGap};
    flex-direction: column;
    position: relative;
    flex: 1;

    ${muiInputFormsCSS}

    .level-details {
        display: flex;
        gap: ${formGap};
        align-items: end;
        flex-wrap: wrap;
        width: calc(100% - ${iconButtonSize});
    }

    .perimeter-input {
        width: 100%;
    }
`;

const StyledFormButtonsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const GeoReferencerContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 500px;
    flex: 1;
`;
